import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/globals/plan-cohort-purse-mappings`.
 *
 * Plan-cohort-purse mappings are the global allocation templates that say
 * "for plan X and cohort Y, route a consumer's reward into purse Z at
 * allocation_percent P". Tenants opt into these per program year — the
 * global surface is the source-of-truth for the allocation grid itself.
 *
 * Unlike the other globals the "code" surface is a synthetic composite
 * (plan_code + cohort_code + purse_code + effective_date). The backend
 * exposes a single `mapping_code` string of the form
 * `{plan}:{cohort}:{purse}:{YYYYMMDD}` that the edit page treats as the
 * immutable key. Versioning model still mirrors the other globals
 * (`current_version` draft → publish → tenants sync) and the 409
 * `{error:'version_conflict', current_version}` shape is identical.
 */

export interface PlanCohortPurseMappingHistoryRow {
  version: number;
  plan_code: string;
  cohort_code: string;
  purse_code: string;
  allocation_percent: number;
  effective_date: string;
  platform_mapping_id: string | number | null;
  active: boolean;
  change_summary: string | null;
  change_type: 'create' | 'update' | 'delete' | 'publish' | 'rollback';
  created_at: string;
  created_by: string | null;
}

export interface AdminPlanCohortPurseMappingRow {
  mapping_code: string;
  plan_code: string;
  cohort_code: string;
  purse_code: string;
  /** 0 – 100 inclusive; stored as a numeric on the backend. */
  allocation_percent: number;
  /** ISO-8601 date (YYYY-MM-DD). */
  effective_date: string;
  platform_mapping_id: string | number | null;
  active: boolean;
  current_version: number;
  updated_at: string;
  updated_by: string | null;
  status: 'in_sync' | 'never_published' | 'unpublished';
}

export interface AdminPlanCohortPurseMappingDetail
  extends AdminPlanCohortPurseMappingRow {
  history: PlanCohortPurseMappingHistoryRow[];
}

export interface ListMappingsResponse {
  rows: AdminPlanCohortPurseMappingRow[];
}

export interface ListMappingsParams {
  sort?: 'plan_code' | 'cohort_code' | 'effective_date' | 'updated_at';
  dir?: 'asc' | 'desc';
  status?: 'in_sync' | 'never_published';
}

export interface CreateMappingPayload {
  plan_code: string;
  cohort_code: string;
  purse_code: string;
  allocation_percent: number;
  effective_date: string;
  change_summary?: string;
}

export interface UpdateMappingPayload {
  base_version: number;
  /**
   * Only mutable fields listed — plan/cohort/purse/effective_date form the
   * composite key and can't change after create. Create a new row instead.
   */
  allocation_percent?: number;
  active?: boolean;
  change_summary?: string;
}

/* ------------------------------------------------------------------ */
/*  Publish + Rollback                                                  */
/* ------------------------------------------------------------------ */

export interface PublishMappingEntityRequest {
  mapping_code: string;
  base_version: number;
}

export interface PublishMappingsRequest {
  entities: PublishMappingEntityRequest[];
  batch_id?: string;
  change_summary?: string;
}

export interface PublishMappingEntityResult {
  mapping_code: string;
  ok: boolean;
  published_version?: number;
  error?: string;
  message?: string;
  current_version?: number;
}

export interface PublishMappingsResponse {
  batch_id: string;
  results: PublishMappingEntityResult[];
}

export interface RollbackMappingPayload {
  target_version: number;
  base_version: number;
  change_summary?: string;
}

const base = '/api/admin/globals/plan-cohort-purse-mappings';

export const adminPlanCohortPurseMappingApi = {
  list: async (
    params: ListMappingsParams = {},
  ): Promise<AdminPlanCohortPurseMappingRow[]> => {
    const res = await axiosClient.get<ListMappingsResponse>(base, { params });
    return res.data.rows;
  },

  get: async (code: string): Promise<AdminPlanCohortPurseMappingDetail> => {
    const res = await axiosClient.get<AdminPlanCohortPurseMappingDetail>(
      `${base}/${encodeURIComponent(code)}`,
    );
    return res.data;
  },

  create: async (
    payload: CreateMappingPayload,
  ): Promise<AdminPlanCohortPurseMappingRow> => {
    const res = await axiosClient.post<AdminPlanCohortPurseMappingRow>(
      base,
      payload,
    );
    return res.data;
  },

  update: async (
    code: string,
    payload: UpdateMappingPayload,
  ): Promise<AdminPlanCohortPurseMappingRow> => {
    const res = await axiosClient.patch<AdminPlanCohortPurseMappingRow>(
      `${base}/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  publish: async (
    payload: PublishMappingsRequest,
  ): Promise<PublishMappingsResponse> => {
    const res = await axiosClient.post<PublishMappingsResponse>(
      `${base}/publish`,
      payload,
    );
    return res.data;
  },

  publishOne: async (
    code: string,
    base_version: number,
    options?: { batch_id?: string; change_summary?: string },
  ): Promise<PublishMappingsResponse> => {
    return adminPlanCohortPurseMappingApi.publish({
      entities: [{ mapping_code: code, base_version }],
      batch_id: options?.batch_id,
      change_summary: options?.change_summary,
    });
  },

  rollback: async (
    code: string,
    payload: RollbackMappingPayload,
  ): Promise<AdminPlanCohortPurseMappingRow> => {
    const res = await axiosClient.post<AdminPlanCohortPurseMappingRow>(
      `${base}/${encodeURIComponent(code)}/rollback`,
      payload,
    );
    return res.data;
  },
};
